<template>
  <section id="solution" class="relative w-full py-8 md:py-9 lg:py-20 px-4 sm:px-8 lg:px-12 xl:px-[120px] bg-white">
    <!-- Header Section -->
    <div class="flex flex-col items-center text-center mb-8 md:mb-12 lg:mb-16">
      <!-- Badge: text-sm/Medium, #36393E -->
      <h2 class="font-inter text-xs md:text-sm font-medium leading-5 text-[#36393E] mb-2">
        {{ sectionBadge || 'GIẢI PHÁP TOÀN DIỆN' }}
      </h2>
      
      <!-- Main Title: display-xs/Bold, Gradient -->
      <h3 class="font-display text-base md:text-xl lg:text-2xl font-bold leading-6 md:leading-7 lg:leading-8 mb-3 md:mb-4 text-center max-w-[558px]">
        <span class="solution-title-gradient">
          {{ sectionTitle || 'HICHAT - ĐỘT PHÁ AI CHO MỌI DOANH NGHIỆP' }}
        </span>
      </h3>
      
      <!-- Subtitle: text-sm/Regular, #5C636C -->
      <p class="font-inter text-xs md:text-sm font-normal leading-4 md:leading-5 text-[#5C636C] max-w-[650px] mx-auto">
        {{ sectionSubtitle || 'Chúng tôi không chỉ là chatbot. HiCHAT là hệ sinh thái AI thông minh giúp vận hành doanh nghiệp của bạn trơn tru hơn, chuyên nghiệp hơn và hiệu quả hơn bao giờ hết.' }}
      </p>
    </div>

    <!-- Mobile Slider (scrollable with peek) -->
    <div class="md:hidden mb-8 overflow-x-auto overflow-y-hidden solution-slider-scroll">
      <div class="flex gap-4" style="scroll-snap-type: x mandatory;">
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="flex-shrink-0 px-2"
          style="width: calc(100% - 48px); scroll-snap-align: start;"
        >
          <div class="solution-feature-card rounded-2xl p-4 relative overflow-hidden flex flex-col gap-3 min-h-[280px]">
            <!-- Layout: Image left, Title & Description right (same row) -->
            <div class="flex flex-row items-start gap-3 flex-shrink-0">
              <!-- Image - Left side -->
              <div class="flex-shrink-0 w-10 h-10">
                <img
                  :src="getImageForFeature(feature)"
                  :alt="feature.title"
                  class="w-full h-full object-contain"
                />
              </div>

              <!-- Title & Description - Right side -->
              <div class="flex-1 flex flex-col min-h-[60px]">
                <!-- Title -->
                <h4 class="font-inter text-sm font-bold leading-5 text-[#36393E] mb-0 min-h-[40px] flex items-start">
                  {{ feature.title }}
                </h4>

                <!-- Description -->
                <p class="font-inter text-xs font-normal leading-4 text-[#5C636C] mt-1 mb-0 flex-1">
                  {{ feature.description }}
                </p>
              </div>
            </div>

            <!-- Bullet Points -->
            <ul class="space-y-1.5 flex-1 flex flex-col justify-start">
              <li
                v-for="(point, pointIndex) in feature.points"
                :key="pointIndex"
                class="flex items-start gap-2"
              >
                <svg class="w-3 h-[10px] flex-shrink-0 mt-0.5" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.773 0.0251687C8.67661 0.713369 6.00385 2.55381 3.53941 5.70645L2.08285 4.09005C1.85881 3.83409 1.44277 3.83409 1.21861 4.09005L0.146527 5.29053C-0.061553 5.53053 -0.045473 5.88261 0.178567 6.09069L3.47521 9.25929C3.74725 9.51537 4.19533 9.45129 4.38745 9.11529C6.14785 5.93049 8.03629 3.56205 11.237 0.841289C11.621 0.505289 11.269 -0.134911 10.7729 0.0252887L10.773 0.0251687Z" fill="#8326D9"/>
                </svg>
                <span class="font-inter text-xs font-medium leading-4 text-[#36393E]">
                  {{ point }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Feature Cards Grid (Tablet & PC: 2 rows, 2 columns) -->
    <div class="hidden md:grid md:grid-cols-2 gap-5 lg:gap-6 mb-8 md:mb-12 lg:mb-16 items-stretch">
      <div
        v-for="(feature, index) in features"
        :key="index"
        class="solution-feature-card rounded-2xl p-4 md:p-4 lg:p-5 relative overflow-hidden flex flex-col gap-3 md:min-h-full"
      >
        <!-- Layout: Image left, Title & Description right (same row) -->
        <div class="flex flex-row items-start gap-3 flex-shrink-0">
          <!-- Image - Left side: 40px mobile/tablet, 100px PC -->
          <div class="flex-shrink-0 w-10 h-10 lg:w-[100px] lg:h-[100px]">
            <img
              :src="getImageForFeature(feature)"
              :alt="feature.title"
              class="w-full h-full object-contain"
            />
          </div>

          <!-- Title & Description - Right side -->
          <div class="flex-1 flex flex-col md:min-h-[70px]">
            <!-- Title: 20px/Bold, #36393E -->
            <h4 class="font-inter text-base md:text-lg lg:text-xl font-bold leading-6 md:leading-7 lg:leading-[30px] text-[#36393E] mb-0 md:min-h-[56px] lg:min-h-0 flex items-start">
              {{ feature.title }}
            </h4>

            <!-- Description: 14px/Regular, #5C636C -->
            <p class="font-inter text-xs md:text-sm lg:text-sm font-normal leading-4 md:leading-5 text-[#5C636C] mt-1 mb-0 md:min-h-[60px] lg:min-h-0">
              {{ feature.description }}
            </p>
          </div>
        </div>

        <!-- Bullet Points: Separate row, left aligned -->
        <ul class="space-y-2 md:flex-1 md:flex md:flex-col md:justify-start">
          <li
            v-for="(point, pointIndex) in feature.points"
            :key="pointIndex"
            class="flex items-start gap-2"
          >
            <svg class="w-3 h-[10px] md:w-3.5 md:h-[14px] lg:w-4 lg:h-4 flex-shrink-0 mt-0.5 md:mt-1" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.773 0.0251687C8.67661 0.713369 6.00385 2.55381 3.53941 5.70645L2.08285 4.09005C1.85881 3.83409 1.44277 3.83409 1.21861 4.09005L0.146527 5.29053C-0.061553 5.53053 -0.045473 5.88261 0.178567 6.09069L3.47521 9.25929C3.74725 9.51537 4.19533 9.45129 4.38745 9.11529C6.14785 5.93049 8.03629 3.56205 11.237 0.841289C11.621 0.505289 11.269 -0.134911 10.7729 0.0252887L10.773 0.0251687Z" fill="#8326D9"/>
            </svg>
            <span class="font-inter text-xs md:text-sm lg:text-base font-medium leading-4 md:leading-5 lg:leading-6 text-[#36393E]">
              {{ point }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Statistical Cards (4 cards in a row) -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
      <div
        v-for="(stat, index) in statistics"
        :key="index"
        class="solution-stat-card flex flex-col items-center justify-center rounded-2xl p-6 sm:p-8 h-full text-center"
      >
        <!-- Icon -->
        <div class="flex items-center justify-center mb-4">
          <img
            :src="getStatIcon(stat.icon)"
            :alt="stat.subtitle"
            class="w-7 h-7 md:w-10 md:h-10 lg:w-12 lg:h-12"
          />
        </div>

        <!-- Value: 18px/Bold, #0F172A -->
        <h5 class="font-inter text-xs md:text-base lg:text-lg font-bold leading-4 md:leading-6 lg:leading-7 text-[#0F172A] mb-2">
          {{ stat.value }}
        </h5>

        <!-- Subtitle: 16px/Medium, #9DA8B5 -->
        <p class="font-inter text-[10px] md:text-sm lg:text-base font-medium leading-3 md:leading-5 lg:leading-6 text-[#9DA8B5]">
          {{ stat.subtitle }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const sectionContent = computed(() => props.content || {});

// Section header - dynamic from admin
const sectionBadge = computed(() => sectionContent.value.badge || sectionContent.value.label || 'GIẢI PHÁP TOÀN DIỆN');
const sectionTitle = computed(() => sectionContent.value.headline || sectionContent.value.title || 'HICHAT - ĐỘT PHÁ AI CHO MỌI DOANH NGHIỆP');
const sectionSubtitle = computed(() => sectionContent.value.subtitle || sectionContent.value.description || 'Chúng tôi không chỉ là chatbot. HiCHAT là hệ sinh thái AI thông minh giúp vận hành doanh nghiệp của bạn trơn tru hơn, chuyên nghiệp hơn và hiệu quả hơn bao giờ hết.');

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

// Parse points from string (each line is a point) or array
const parsePoints = (points) => {
  if (Array.isArray(points)) return points.filter(Boolean);
  if (typeof points === 'string') {
    return points.split('\n').map(p => p.trim()).filter(Boolean);
  }
  return [];
};

// Icon to image mapping
const iconImageMap = {
  'automation': '/landing/agent.png',
  'omnichannel': '/landing/ommichannel.png',
  'multi_channel': '/landing/ommichannel.png',
  'ai': '/landing/agent.png',
  'ai_chatbot': '/landing/agent.png',
  'data': '/landing/data-collect.png',
  'data_collection': '/landing/data-collect.png',
  'analytics': '/landing/report.png',
  'reports': '/landing/report.png',
  'integration': '/landing/data-collect.png',
  'support': '/landing/agent.png',
};

// Default features data
const defaultFeatures = [
  {
    icon: 'omnichannel',
    title: 'Hợp Nhất Đa Kênh (Omnichannel)',
    description: 'Tập trung hội thoại từ Website, Facebook, Zalo, Etsy, Telegram và WhatsApp trên một nền tảng duy nhất, không bỏ lỡ tin nhắn khách hàng.',
    points: [
      'Quản lý tập trung 100% tin nhắn',
      'Đồng bộ trò chuyện thời gian thực',
      'Hỗ trợ đa nền tảng: Facebook, Zalo, WhatsApp, Website'
    ],
  },
  {
    icon: 'ai',
    title: 'Trợ Lý AI Thông Minh 24/7',
    description: 'Tự động hóa 80% câu hỏi thường gặp (FAQs) với độ chính xác cao. AI học sâu từ dữ liệu doanh nghiệp để tư vấn như một chuyên viên thực thụ.',
    points: [
      'Trả lời ngay lập tức 24/7/365',
      'Cá nhân hóa theo từng tệp khách hàng',
      'Khả năng xử lý đa ngôn ngữ vượt trội'
    ],
  },
  {
    icon: 'data_collection',
    title: 'Thu thập & đồng bộ dữ liệu khách hàng tự động',
    description: 'AI thu thập dữ liệu theo form hoặc từ nội dung hội thoại, rồi tự động đẩy về hệ thống lưu trữ của doanh nghiệp bạn.',
    points: [
      'Kết nối Google Sheets / LarkSuite / CRM',
      'Tự dừng khi đã đủ thông tin (SĐT, nhu cầu...)',
      'Đồng bộ dữ liệu theo thời gian thực'
    ],
  },
  {
    icon: 'analytics',
    title: 'Báo cáo & phân tích hiệu quả hội thoại',
    description: 'Cung cấp báo cáo trực quan về hiệu suất từng kênh, nhân viên và thời gian phản hồi để tối ưu quy trình.',
    points: [
      'Dashboard realtime sinh động',
      'So sánh lưu lượng theo ngày/tuần/kênh',
      'Báo cáo SLA, đánh giá hiệu quả CSKH'
    ],
  },
];

// Features data - dynamic from admin or fallback to defaults
const features = computed(() => {
  const items = toArray(sectionContent.value.features || sectionContent.value.primary);
  if (items.length === 0) return defaultFeatures;
  
  // Map admin data to feature format
  return items.filter(item => item?.title).map(item => ({
    icon: item.icon || 'automation',
    title: item.title,
    description: item.description || '',
    points: parsePoints(item.points || item.highlights || []),
  }));
});

// Get image for feature based on icon key or title
const getImageForFeature = (feature) => {
  // First try icon mapping
  if (feature.icon && iconImageMap[feature.icon]) {
    return iconImageMap[feature.icon];
  }
  
  // Fallback to title-based detection
  const titleLower = (feature.title || '').toLowerCase();
  if (titleLower.includes('kênh') || titleLower.includes('omnichannel')) {
    return '/landing/ommichannel.png';
  }
  if (titleLower.includes('ai') || titleLower.includes('trợ lý') || titleLower.includes('agent')) {
    return '/landing/agent.png';
  }
  if (titleLower.includes('dữ liệu') || titleLower.includes('data') || titleLower.includes('thu thập')) {
    return '/landing/data-collect.png';
  }
  if (titleLower.includes('báo cáo') || titleLower.includes('report') || titleLower.includes('phân tích')) {
    return '/landing/report.png';
  }
  
  return '/landing/agent.png';
};

// Statistics data
const statistics = computed(() => {
  const items = toArray(sectionContent.value.statistics || sectionContent.value.metrics);
  return items.length > 0 ? items : [
    {
      value: 'Tăng 60%',
      subtitle: 'Tốc Độ Phản hồi tức thì',
      icon: 'rocket',
    },
    {
      value: 'Tăng 45%',
      subtitle: 'CSAT Khách hàng hài lòng',
      icon: 'star',
    },
    {
      value: 'X3',
      subtitle: 'Tối ưu phễu bán hàng',
      icon: 'trending-up',
    },
    {
      value: 'Insight Thực Tế',
      subtitle: 'Báo cáo hành vi 24/7',
      icon: 'brain',
    },
  ];
});

const statIconMap = {
  'rocket': '/landing/icon/rocket.svg',
  'star': '/landing/icon/like-star.svg',
  'trending-up': '/landing/icon/coin.svg',
  'brain': '/landing/icon/dashboard.svg',
  'zap': '/landing/icon/rocket.svg',
  'sparkles': '/landing/icon/like-star.svg',
  'chart': '/landing/icon/dashboard.svg',
};

const getStatIcon = (iconKey) => {
  if (!iconKey) return '/landing/icon/dashboard.svg';
  return statIconMap[iconKey] || '/landing/icon/dashboard.svg';
};

</script>

